/**
 * What a gRPC tab has LOADED — the reflected / .proto descriptor (and the .proto's
 * path, which calls are encoded with) and the picked service + method — kept per
 * TAB, outside the component tree (card b9799baf).
 *
 * A tab switch unmounts GRPCBuilder, and these lived in its component state:
 * coming back, the definition was gone and the method had to be picked again.
 * Like a WebSocket's unsent message (`liveConnections` drafts), this belongs to
 * the open tab, not to the saved request — a descriptor can be large and says
 * what one server answered once. It lives as long as the tab:
 * `pruneGrpcTabState(openTabIds)` runs beside `pruneLiveConnections`.
 */

const states = new Map();

const keyOf = (tabId) => (tabId === undefined || tabId === null ? null : String(tabId));

/** @returns {{ descriptor: object|null, service: string|null, method: string|null, protoPath: string|null } | null} */
export function getGrpcTabState(tabId) {
  return states.get(keyOf(tabId)) ?? null;
}

/** Merge `patch` into the tab's state. */
export function setGrpcTabState(tabId, patch) {
  const key = keyOf(tabId);
  if (key === null) return;
  const current = states.get(key) ?? { descriptor: null, service: null, method: null, protoPath: null };
  states.set(key, { ...current, ...patch });
}

/** Keep only what belongs to a tab that is still open. */
export function pruneGrpcTabState(openTabIds) {
  const open = new Set([...openTabIds].map(String));
  [...states.keys()].filter((key) => !open.has(key)).forEach((key) => states.delete(key));
}
